import { useState, useContext, useEffect } from "react";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from "../../context/UserContext";
import { ProfileSchema } from "../../validationSchema/profileSchema";
import {
  deleteImage,
  deleteUser,
  getUser,
  updateUser,
  uploadImage,
} from "../../utils/Api";
import ProfileModal from "./profileModal";
import MyProfileView from "./myProfileView";
import "./profile.css";

const defaultImage = "https://cloud-poker-night-worker.cloud-poker-night-dev.workers.dev/cpnDefaultUser.png";

function CreateProfileModal() {
  const { user, setUser, setShowProfileModal, setTokens } = useContext(UserContext);
  const [show, setShow] = useState(false);
  const [file, setFile] = useState();
  const [isLoading, setIsLoading] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const initProfileData = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    linkedIn: user?.linkedIn || "",
    companyName: user?.companyName || "",
    aboutMe: user?.aboutMe || "",
    title: user?.title || "",
    twitter: user?.twitter || "",
    instagram: user?.instagram || "",
  };
  const [profileFormData, setProfileFormData] = useState(initProfileData);
  const [deleteFile, setDeleteFile] = useState(false);
  const { gameId } = useParams();
  const navigate = useNavigate();
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [imageData, setImageData] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const auth0 = localStorage.getItem("auth");
  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setError,
  } = useForm({
    resolver: yupResolver(ProfileSchema),
    defaultValues: { user, gameId, auth0: JSON.parse(auth0) },
  });

  const callBackUserUpdate = (data) => {
    setUser(data.user);
  };

  const handleSaveFile = async (imageFile) => {
    setIsLoading("update");
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await uploadImage(formData);
    if (res?.message === "Please select jpg or png image type.") {
      setError(
        "image",
        { type: "custom", message: "Please select jpg/jpeg or png type image" },
        { shouldFocus: true }
      );
    }
    if (res) {
      getUser(callBackUserUpdate);
      setIsLoading("");
      setImagePreview(file);
    }
  };

  const handleUpdate = async (values) => {
    setIsLoading("update");

    try {
      if (deleteFile) {
        await deleteImage();
        getUser(callBackUserUpdate);
      }

      const payload = { ...values, auth0: JSON.parse(auth0) };
      if (imageData) {
        await handleSaveFile(imageData);
      }

      const res = await updateUser(payload);

      if (res) {
        setDeleteFile(false);
        setUser(res);
        setShowProfileModal(false);
        setIsEditable(false);
        navigate(window.location.pathname);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error during profile update:", error);
    } finally {
      setIsLoading("");
    }
  };

  const imageName = user?.avatar?.substring(user.avatar.lastIndexOf("/") + 1);

  const handleClick = () => {
    setShowProfileModal(false);
    setDeleteFile(false);
    setIsEditable(false);
    navigate(window.location.pathname);
  };

  const handleFileChange = (e) => {
    const file1 = e.target.files[0];
    setImagePreview(URL.createObjectURL(file1));
    setFile(file1);
    setImageData(file1);
  };

  const handleSetDeleteFile = async () => {
    setDeleteFile(true);
    setImagePreview(
      "https://cloud-poker-night-worker.cloud-poker-night-dev.workers.dev/cpnDefaultUser.png"
    );
    setIsLoading("save");
  };

  useEffect(() => {
    const updateProfileData = {
      firstName: watch("firstName") ? watch("firstName") : user?.firstName,
      lastName: watch("lastName") ? watch("lastName") : user?.lastName,
      email: watch("email") ? watch("email") : user?.email,
      companyName: watch("companyName")
        ? watch("companyName")
        : user?.companyName,
      linkedIn: watch("linkedIn") ? watch("linkedIn") : user?.linkedIn,
      aboutMe: watch("aboutMe") ? watch("aboutMe") : user?.aboutMe,
      title: watch("title") ? watch("title") : user?.title,
      twitter: watch("twitter") ? watch("twitter") : user?.twitter,
      instagram: watch("instagram") ? watch("instagram") : user?.instagram,
    };
    setProfileFormData(updateProfileData);
  }, [
    watch("firstName"),
    watch("lastName"),
    watch("email"),
    watch("companyName"),
    watch("aboutMe"),
    watch("title"),
    watch("linkedIn"),
    watch("twitter"),
    watch("instagram"),
  ]);

  const handleShowAccountDelete = () => {
    if (gameId) {
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteAccount = async () => {
    setIsLoading("delete");
    const response = await deleteUser();
    if (response.code === 200) {
      localStorage.clear();
      setTokens();
      setUser({});
      setIsLoading("");
      navigate("/login");
    }
  };

  return isEditable ? (
    <div className="register-profile-modal">
      <div className="create-profile-page">
        <div className="create-profile">
          <div className="create-profile-content">
            <Form onSubmit={handleSubmit(handleUpdate)}>
              <div className="create-profile-left">
                <h3>Update profile</h3>

                <div className="create-profile-grid">
                  <div className="profile-pic-upload">
                    {errors?.image ? (
                      <p className="error-text">{errors.image.message}</p>
                    ) : (
                      ""
                    )}
                    <div className="profile-pic">
                      <img
                        src={imagePreview || user?.avatar || defaultImage}
                        onError={({ currentTarget }) => {
                          // eslint-disable-next-line no-param-reassign
                          currentTarget.onerror = null; // prevents looping
                          // eslint-disable-next-line no-param-reassign
                          currentTarget.src = defaultImage;
                        }}
                        alt="User Profile"
                      />
                    </div>
                    <div className="upload-btn">
                      <Form.Control
                        type="file"
                        accept="image/png, image/jpeg"
                        onChange={handleFileChange}
                        multiple={false}
                      />
                    </div>
                    <div className="delete-btn">
                      {imagePreview
                      || imageName === "user.png"
                      || imageName === "cpnDefaultUser.png" ? (
                          ""
                        ) : (
                          <Button
                            className="dlt-btn"
                            onClick={handleSetDeleteFile}
                            disabled={
                            imageName === "user.png"
                            || imageName === "cpnDefaultUser.png"
                          }
                          >
                            {isLoading === "delete" ? (
                              <Spinner animation="border" />
                            ) : (
                              "Delete"
                            )}
                          </Button>
                        )}
                    </div>
                  </div>
                  <div className="profile-form">
                    <div className="half-width">
                      <Form.Group className="form-group">
                        <Form.Label>First name</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          maxLength={26}
                          className={errors.firstName ? "error-field" : ""}
                          defaultValue={user?.firstName}
                          {...register("firstName")}
                        />
                        {errors?.firstName ? (
                          <p className="error-text">
                            {errors.firstName.message}
                          </p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          maxLength={26}
                          defaultValue={user?.title}
                          className={errors.title ? "error-field" : ""}
                          {...register("title")}
                        />
                        {errors?.title ? (
                          <p className="error-text">{errors.title.message}</p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>Last name</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          maxLength={26}
                          defaultValue={user?.lastName}
                          className={errors.lastName ? "error-field" : ""}
                          {...register("lastName")}
                        />
                        {errors?.lastName ? (
                          <p className="error-text">
                            {errors.lastName.message}
                          </p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>LinkedIn (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="LinkedIn username"
                          name="linkedIn"
                          defaultValue={user?.linkedIn}
                          className={errors.linkedIn ? "error-field" : ""}
                          {...register("linkedIn")}
                        />
                        {errors?.linkedIn ? (
                          <p className="error-text">
                            {errors.linkedIn.message}
                          </p>
                        ) : (
                          ""
                        )}
                      </Form.Group>
                      <Form.Group className="form-group">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          readOnly
                          name="email"
                          defaultValue={user?.email}
                          className={errors.email ? "error-field" : ""}
                          {...register("email")}
                        />
                        {errors?.email ? (
                          <p className="error-text">{errors.email.message}</p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>Instagram (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Instagram username"
                          name="instagram"
                          defaultValue={
                            user?.instagram ? user?.instagram : null
                          }
                          className={errors.instagram ? "error-field" : ""}
                          {...register("instagram")}
                        />
                        {errors?.instagram ? (
                          <p className="error-text">
                            {errors.instagram.message}
                          </p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>Company Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyName"
                          maxLength={50}
                          defaultValue={user?.companyName}
                          className={errors.companyName ? "error-field" : ""}
                          {...register("companyName")}
                        />
                        {errors?.companyName ? (
                          <p className="error-text">
                            {errors.companyName.message}
                          </p>
                        ) : (
                          ""
                        )}
                      </Form.Group>

                      <Form.Group className="form-group">
                        <Form.Label>Twitter (Optional)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Twitter username"
                          name="twitter"
                          defaultValue={user?.twitter ? user.twitter : null}
                          className={errors.twitter ? "error-field" : ""}
                          {...register("twitter")}
                        />
                        {errors?.twitter ? (
                          <p className="error-text">{errors.twitter.message}</p>
                        ) : (
                          ""
                        )}
                      </Form.Group>
                    </div>
                  </div>
                </div>
              </div>

              <div className="create-profile-right">
                <div className="create-profile-right-wrapper">
                  <div className="profile-btns">
                    <span onClick={handleShow} role="presentation">
                      Profile preview
                    </span>
                    <Button
                      className="profile-outline-btn profile-bg-btn"
                      onClick={handleClick}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="profile-bg-btn"
                      type="submit"
                      disabled={isLoading === "update"}
                    >
                      {isLoading === "update" ? (
                        <Spinner animation="border" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                  <div className="create-profile-right-section">
                    <Form.Group className="form-group">
                      <Form.Label>About me</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="aboutMe"
                        defaultValue={user?.aboutMe}
                        {...register("aboutMe")}
                      />
                      {errors?.aboutMe ? (
                        <p className="error-text">{errors.aboutMe.message}</p>
                      ) : (
                        ""
                      )}
                    </Form.Group>
                    <Form.Check
                      className="add-check-box"
                      type="checkbox"
                      label="Allow to make public Twitter, Instagram and LinkedIn links"
                      id="social-public"
                      defaultChecked={user?.isLinkPublic}
                      {...register("isLinkPublic")}
                    />
                  </div>
                </div>
                <Button
                  className="delete-btn"
                  onClick={handleShowAccountDelete}
                  disabled={gameId}
                >
                  Delete account
                </Button>
              </div>
            </Form>
          </div>
        </div>

        <Modal
          show={show}
          onHide={handleClose}
          className="preview-modal"
          backdropClassName="profile-preview-modal-back"
          centered
        >
          <Modal.Header closeButton />
          <Modal.Body>
            <ProfileModal
              imagePreview={imagePreview}
              user={user}
              profileFormData={profileFormData}
            />
          </Modal.Body>
        </Modal>
        <Modal
          show={showDeleteModal}
          onHide={handleDeleteClose}
          className="preview-modal account-delete-modal"
          backdropClassName="profile-preview-modal-back"
          centered
        >
          <Modal.Body>
            <div className="delete-popup-text">
              <h6>Are you sure you want to delete?</h6>
              <p>
                Your account and all of your personal data will be deleted. Once
                completed this action cannot be undone. Account recovery is not
                possible.
              </p>
              <div className="bar-room-leave-btn">
                <Button className="bg-btn" onClick={handleDeleteAccount}>
                  {isLoading === "delete" ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Delete"
                  )}
                </Button>
                <Button
                  className=" bg-btn outline-btn"
                  onClick={handleDeleteClose}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  ) : (
    <MyProfileView setIsEditable={setIsEditable} user={user} />
  );
}

export default CreateProfileModal;
